import { Box } from "@mui/material";
import { useFadeInAnimation } from "../../hooks/useFadeInAnimation";

/**
 * Reusable component that applies fade-in and slide-up animation
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child elements to animate
 * @param {number} props.delay - Animation delay in seconds (default: 0)
 * @param {number} props.duration - Animation duration in seconds (default: 0.8)
 * @param {number} props.translateY - Initial translateY value in pixels (default: 30)
 * @param {boolean} props.isVisible - External visibility state (optional, for use with FadeInContainer)
 * @param {boolean} props.triggerOnMount - Whether to trigger animation on mount (default: true)
 * @param {boolean} props.triggerOnScroll - Whether to trigger animation on scroll (default: true)
 * @param {number} props.threshold - Intersection Observer threshold (default: 0.1)
 * @param {Object} props.animationOptions - Options for useFadeInAnimation hook (ignored if isVisible is provided)
 * @param {Object} props.sx - Additional Material-UI sx styles
 * @param {Object} props.rest - Other props to pass to Box component
 */
const FadeInBox = ({
  children,
  delay = 0,
  duration = 0.8,
  translateY = 30,
  isVisible: externalIsVisible,
  triggerOnMount,
  triggerOnScroll,
  threshold,
  animationOptions = {},
  sx = {},
  ...rest
}) => {
  // Merge direct props with animationOptions
  const mergedAnimationOptions = {
    ...animationOptions,
    ...(triggerOnMount !== undefined && { triggerOnMount }),
    ...(triggerOnScroll !== undefined && { triggerOnScroll }),
    ...(threshold !== undefined && { threshold }),
  };

  // Use external visibility state if provided, otherwise use hook
  const { ref, isVisible: hookIsVisible } = useFadeInAnimation(
    externalIsVisible !== undefined ? {} : mergedAnimationOptions
  );
  const isVisible = externalIsVisible !== undefined ? externalIsVisible : hookIsVisible;
  const elementRef = externalIsVisible !== undefined ? undefined : ref;

  return (
    <Box
      ref={elementRef}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : `translateY(${translateY}px)`,
        transition: `opacity ${duration}s ease-out, transform ${duration}s ease-out`,
        transitionDelay: `${delay}s`,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default FadeInBox;
